// @givmo/org-console — the reads and writes, as hooks (fifteen at OM-10; +8 at shell contract v3).
//
// Every one is scoped by `orgRef` and every one goes through `org-console-transport.ts`, so the URL
// is written in exactly one place and the tolerant decode in exactly one other.
//
// ⚠ PAGING IS "DID THE LAST PAGE COME BACK SHORT". The paginated envelope's `meta` is discarded by
// the unwrap before a caller ever sees it, so there is no total to page against and no screen here
// may claim one. See `org-decode.ts#nextOffset`.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  accessRequestApprovalInvalidations,
  accessRequestCreateInvalidations,
  accessRequestDenialInvalidations,
  accessWriteInvalidations,
  connectorWriteInvalidations,
  inviteWriteInvalidations,
  measurementWriteInvalidations,
  orgAccessRequestsKey,
  orgInvitesKey,
  orgConsoleKeys,
  orgInvitePreviewKey,
  orgInviteRedeemKey,
  orgModuleTermsKey,
  orgSettingWriteInvalidations,
  preferenceWriteInvalidations,
  removalInvalidations,
  termsAcceptInvalidations,
} from './org-access-queries';
import { isAlreadyNotAMember, isStaleRoleChangeRefusal } from './org-access-model';
import {
  decodeOrgAccessEvents,
  decodeOrgAccessRequest,
  decodeOrgAccessRequests,
  decodeOrgAccessSettings,
  decodeOrgAdminPreferences,
  decodeOrgCatalog,
  decodeOrgConnectedAi,
  decodeOrgDeactivation,
  decodeOrgInviteAccepted,
  decodeOrgInviteCreated,
  decodeOrgInvitePreview,
  decodeOrgInvites,
  decodeOrgMeasurementConfig,
  decodeOrgMember,
  decodeOrgMembers,
  decodeOrgModuleTerms,
  decodeOrgSelfAccess,
  decodeOrgSettings,
  nextOffset,
} from './org-decode';
import {
  ORG_CONSOLE_EVENTS_PAGE_LIMIT,
  ORG_CONSOLE_PAGE_LIMIT,
  acceptOrgInvite,
  acceptOrgModuleTerms,
  approveOrgAccessRequest,
  changeOrgMemberRole,
  createOrgAccessRequest,
  createOrgInvite,
  deactivateOrg,
  denyOrgAccessRequest,
  fetchOrgAccessCatalog,
  fetchOrgAccessEvents,
  fetchOrgAccessRequests,
  fetchOrgAccessSettings,
  fetchOrgAdminPreferences,
  fetchOrgConnectedAi,
  fetchOrgInvites,
  fetchOrgMeasurementConfig,
  fetchOrgMembers,
  fetchOrgModuleTerms,
  fetchOrgSelfAccess,
  fetchOrgSettings,
  isOrgConsoleUnavailable,
  previewOrgInvite,
  removeOrgMember,
  revokeOrgConnectedAi,
  revokeOrgInvite,
  updateOrgAccessSettings,
  updateOrgAdminPreferences,
  updateOrgMeasurementConfig,
  updateOrgMemberAccess,
  updateOrgSettings,
} from './org-console-transport';
import type {
  AcceptOrgModuleTermsBody,
  ApproveOrgAccessRequestBody,
  CreateOrgAccessRequestBody,
  CreateOrgInviteBody,
  DenyOrgAccessRequestBody,
  OrgAccessEventRow,
  OrgAccessRequestRow,
  OrgAccessSettings,
  OrgAdminPreferences,
  OrgConnectedAiRow,
  OrgConsoleMemberRow,
  OrgInviteAccepted,
  OrgInvitePreview,
  OrgDeactivationResult,
  OrgInviteCreated,
  OrgInviteRow,
  OrgMeasurementConfig,
  OrgModuleCatalogRow,
  OrgModuleTerms,
  OrgRoleChangeBody,
  OrgSelfAccess,
  OrgSettings,
  UpdateOrgAccessSettingsBody,
  UpdateOrgAdminPreferencesBody,
  UpdateOrgMeasurementConfigBody,
  UpdateOrgMemberAccessBody,
  UpdateOrgSettingsBody,
} from './wire-types';

/**
 * A read that is dark in this environment must not be retried. The answer will not change, and a
 * retry only spends a second request to land on the same calm not-available state.
 */
function retryUnlessAbsent(failureCount: number, error: Error): boolean {
  return !isOrgConsoleUnavailable(error) && failureCount < 1;
}

// ── The caller's own standing — the SINGLE reader ────────────────────────────

/**
 * The caller's own standing for THIS organization. **The single reader of `/me` in this package, and
 * the only place any surface learns what its viewer may do.**
 *
 * ⚠ NEVER DERIVE THIS ANYWHERE ELSE. A host's own membership role is a fact about that host's model,
 * not about this console's tier, and the two can legitimately disagree — a second answer to "what may
 * I do here" is the drift the org-machinery rule exists to stop. The backend says as much on the
 * route itself: it is deliberately the only such answer.
 *
 * ⚠ `retryOnMount` IS OFF, AND THAT IS A STRUCTURAL DEFENCE AGAINST A MEASURED INFINITE LOOP. On the
 * surface this pattern comes from, a second observer of the same `/me` key was mounted CONDITIONALLY
 * on the first one's state — so when the read settled, the second observer appeared, retried the
 * errored query on mount, flipped the shell back to pending, unmounted itself, and started again. It
 * pinned the CPU and blanked the screen on every failed read. Measured, not theorised. Two things
 * keep it from recurring here: this package owns the one reader, and an errored query cannot be
 * re-fired by a late-arriving observer at all — a retry is an explicit act through
 * {@link useOrgAccessGate}'s `refetch`, which is also the only honest place to offer one.
 */
export function useOrgSelfAccess(orgRef: string): UseQueryResult<OrgSelfAccess | null> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).self,
    queryFn: async ({ signal }) =>
      decodeOrgSelfAccess(await fetchOrgSelfAccess(orgRef, { signal })),
    retry: retryUnlessAbsent,
    retryOnMount: false,
  });
}

/** The organization's time zone setting (`org.time_zone`, OM-17's registry), changed on Settings. */
export const ORG_TIME_ZONE_SETTING = 'org.time_zone';

/**
 * The organization's time zone in force (IANA) off the same `/me` query, for a host whose own standing
 * read is a class-specific self read rather than `/org/{org_ref}/me`: one shared request under the one
 * key, never a second read. `null` until known, or where the organization has none.
 * Waits while `orgRef` is `null`.
 */
export function useOrgTimeZone(orgRef: string | null): string | null {
  const read = useQuery({
    queryKey: orgConsoleKeys(orgRef ?? '').self,
    queryFn: async ({ signal }) =>
      decodeOrgSelfAccess(await fetchOrgSelfAccess(orgRef as string, { signal })),
    enabled: orgRef !== null,
    retry: retryUnlessAbsent,
    retryOnMount: false,
  });
  const zone = read.data?.time_zone;
  return typeof zone === 'string' && zone.length > 0 ? zone : null;
}

/** The three flavours of "may this person do that", derived from the one `/me` read. */
export interface OrgAccessGate {
  self: OrgSelfAccess | null;
  /** The read is still in flight. */
  isPending: boolean;
  /**
   * PERMISSIVE gate, for write CONTROLS: true while the read is in flight or errored, so a transient
   * failure never strands a real Global Admin. The server's own refusal is the authoritative
   * backstop, so a permissive client gate changes nothing a Member can actually do — while a
   * restrictive one locks a real Global Admin out of their own team over a flaky request.
   */
  isGlobalAdmin: boolean;
  /**
   * STRICT gate, for Global-Admin-only READS: requires a RESOLVED `is_global_admin: true`. Gating a
   * fetch on the permissive flag fires a request the backend refuses.
   */
  isGlobalAdminConfirmed: boolean;
  /**
   * The read FAILED, so this person's standing is genuinely unknown.
   *
   * ⚠ Distinct from `!isGlobalAdminConfirmed`, which is also true for a resolved Member. A surface
   * gating on the strict flag alone tells an admin "only a Global Admin can do this" after a network
   * error — asserting something it never learned. Both states fail closed; only this one is
   * recoverable, so only this one offers a retry.
   */
  tierUnknown: boolean;
  /** The console is absent in this environment. A calm state, never an error. */
  isUnavailable: boolean;
  /** Re-run the read. The retry offered alongside {@link OrgAccessGate.tierUnknown}. */
  refetch: () => void;
}

/** Derive the gate flavours from the single `/me` reader. */
export function useOrgAccessGate(orgRef: string): OrgAccessGate {
  const query = useOrgSelfAccess(orgRef);
  const resolved = query.isSuccess && query.data !== null;
  const isGlobalAdmin = query.data?.is_global_admin === true;
  return {
    self: query.data ?? null,
    isPending: query.isPending,
    // Permissive: false ONLY for a RESOLVED non-admin.
    isGlobalAdmin: !(resolved && !isGlobalAdmin),
    isGlobalAdminConfirmed: resolved && isGlobalAdmin,
    // Settled but not successfully. Deliberately NOT `!isSuccess`, which is also true while the read
    // is still in flight — that is `isPending`, a different screen.
    tierUnknown: query.isError && !isOrgConsoleUnavailable(query.error),
    isUnavailable: query.isError && isOrgConsoleUnavailable(query.error),
    refetch: () => void query.refetch(),
  };
}

// ── Lists ────────────────────────────────────────────────────────────────────

/** The admin roster, paged. Any admin of the organization may read it. */
export function useOrgMembers(orgRef: string): UseInfiniteQueryResult<OrgConsoleMemberRow[]> {
  const queryKey = orgConsoleKeys(orgRef).members;
  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) =>
      decodeOrgMembers(await fetchOrgMembers(orgRef, { offset: pageParam, signal })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      nextOffset(
        lastPage.length,
        allPages.reduce((n, page) => n + page.length, 0),
        ORG_CONSOLE_PAGE_LIMIT,
      ),
    select: (data) => data.pages.flat(),
    retry: retryUnlessAbsent,
  });
}

/**
 * The module catalog. A PAGED LIST, exhausted, because the editor's rows ARE the catalog — a
 * truncated one would silently offer a narrower set of modules than the organization actually has.
 */
export function useOrgAccessCatalog(
  orgRef: string,
  enabled = true,
): UseQueryResult<OrgModuleCatalogRow[]> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).catalog,
    enabled,
    queryFn: async ({ signal }) => {
      const rows: OrgModuleCatalogRow[] = [];
      let offset = 0;
      for (;;) {
        const page = decodeOrgCatalog(await fetchOrgAccessCatalog(orgRef, { offset, signal }));
        rows.push(...page);
        const next = nextOffset(page.length, rows.length, ORG_CONSOLE_PAGE_LIMIT);
        if (next === undefined) return rows;
        offset = next;
      }
    },
    retry: retryUnlessAbsent,
  });
}

/** The org-wide module switches. Global-Admin only. */
export function useOrgAccessSettings(
  orgRef: string,
  enabled = true,
): UseQueryResult<OrgAccessSettings | null> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).settings,
    enabled,
    queryFn: async ({ signal }) =>
      decodeOrgAccessSettings(await fetchOrgAccessSettings(orgRef, { signal })),
    retry: retryUnlessAbsent,
  });
}

/**
 * The access event log, newest first, paged. Global-Admin only — so the caller gates this on the
 * STRICT flag: firing it on the permissive one spends a request the backend refuses.
 */
export function useOrgAccessEvents(
  orgRef: string,
  enabled = true,
): UseInfiniteQueryResult<OrgAccessEventRow[]> {
  const queryKey = orgConsoleKeys(orgRef).events;
  return useInfiniteQuery({
    queryKey,
    enabled,
    queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) =>
      decodeOrgAccessEvents(await fetchOrgAccessEvents(orgRef, { offset: pageParam, signal })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      nextOffset(
        lastPage.length,
        allPages.reduce((n, page) => n + page.length, 0),
        ORG_CONSOLE_EVENTS_PAGE_LIMIT,
      ),
    select: (data) => data.pages.flat(),
    retry: retryUnlessAbsent,
  });
}

/**
 * The organization's measurement ids. A plain object read, not a list, and not gated.
 *
 * ⚠ NO `enabled` PARAMETER, DELIBERATELY, unlike the Global-Admin-only reads above. The backend
 * serves this to any admin of the organization, so gating the fetch on a standing flag would make
 * the card wait on `/me` for a read that never needed it — and would render an empty card during a
 * standing read that is only in flight.
 */
export function useOrgMeasurementConfig(
  orgRef: string,
): UseQueryResult<OrgMeasurementConfig | null> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).measurement,
    queryFn: async ({ signal }) =>
      decodeOrgMeasurementConfig(await fetchOrgMeasurementConfig(orgRef, { signal })),
    retry: retryUnlessAbsent,
  });
}

/**
 * This organization's LIVE invites, newest first, paged — or, with `module`, the offers that carry
 * ONE app (a module's own Team page; the server scopes it).
 *
 * ⚠ THE CALLER GATES THIS, on the settled standing read: the org-wide list on the STRICT flag (the
 * operation carries the same authority as making an invite), a module's list on that OR the
 * module's `<module>:invite_admins` string. Firing it on a guess spends a request the backend
 * refuses.
 */
export function useOrgInvites(
  orgRef: string,
  enabled = true,
  module: string | null = null,
): UseInfiniteQueryResult<OrgInviteRow[]> {
  const queryKey = orgInvitesKey(orgRef, module);
  return useInfiniteQuery({
    queryKey,
    enabled,
    queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) =>
      decodeOrgInvites(await fetchOrgInvites(orgRef, { offset: pageParam, module, signal })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      nextOffset(
        lastPage.length,
        allPages.reduce((n, page) => n + page.length, 0),
        ORG_CONSOLE_PAGE_LIMIT,
      ),
    select: (data) => data.pages.flat(),
    retry: retryUnlessAbsent,
  });
}

/** The AIs connected to this organization, newest first, paged. Any admin may read it. */
export function useOrgConnectedAi(orgRef: string): UseInfiniteQueryResult<OrgConnectedAiRow[]> {
  const queryKey = orgConsoleKeys(orgRef).connectedAi;
  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) =>
      decodeOrgConnectedAi(await fetchOrgConnectedAi(orgRef, { offset: pageParam, signal })),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      nextOffset(
        lastPage.length,
        allPages.reduce((n, page) => n + page.length, 0),
        ORG_CONSOLE_PAGE_LIMIT,
      ),
    select: (data) => data.pages.flat(),
    retry: retryUnlessAbsent,
  });
}

// ── Writes ───────────────────────────────────────────────────────────────────

/** What one member-access write needs. The body is the DIFF from `org-access-model.ts`. */
export interface UpdateOrgMemberAccessArgs {
  targetUserId: string;
  body: UpdateOrgMemberAccessBody;
}

/**
 * Patch one admin's access.
 *
 * The mutation resolves to the SERVER's re-resolved row — not the draft the form held. The backend
 * applies the tier change first and re-resolves after it, so the answer is frequently not what the
 * form believed it was submitting; a caller that wrote its draft into the list would show a state
 * that never existed.
 */
export function useUpdateOrgMemberAccess(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<OrgConsoleMemberRow | null, Error, UpdateOrgMemberAccessArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: UpdateOrgMemberAccessArgs) =>
      decodeOrgMember(await updateOrgMemberAccess(orgRef, args.targetUserId, args.body)),
    onSuccess: () => {
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of accessWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      // The host's own derived reads, LAST and never awaited, for the same reason — see
      // `OrgConsoleConfig.onAccessWritten`.
      onWritten?.();
    },
  });
}

/** What one role change needs. The body is the CHOICE from `org-access-model.ts`. */
export interface ChangeOrgMemberRoleArgs {
  targetUserId: string;
  body: OrgRoleChangeBody;
}

/**
 * Make one Global Admin a regular admin holding exactly the apps chosen (OM-57, D-CPO-64).
 *
 * Resolves to the SERVER's re-resolved row, as {@link useUpdateOrgMemberAccess} does, and
 * invalidates the same reads: the change moves the person's tier and grants, nothing org-wide.
 *
 * ⚠ TWO REFUSALS SAY THE RECORD MOVED UNDER THE DIALOG — the 409 (they are no longer a Global
 * Admin) and an app withdrawn ({@link isStaleRoleChangeRefusal}) — and their words send the person
 * to Cancel and reopen. So both re-read the roster and the standing now, behind the open dialog
 * (which holds its own row), and mark the catalog stale WITHOUT re-reading it: an open dialog
 * re-primes its rows when its catalog lands, which would wipe the choice the refusal is about. The
 * reopen then reads it afresh. Nothing moved, so the host hears nothing (`onWritten` is a landing).
 */
export function useChangeOrgMemberRole(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<OrgConsoleMemberRow | null, Error, ChangeOrgMemberRoleArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: ChangeOrgMemberRoleArgs) =>
      decodeOrgMember(await changeOrgMemberRole(orgRef, args.targetUserId, args.body)),
    onSuccess: () => {
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of accessWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      // The host's own derived reads, LAST and never awaited, as on the PUT.
      onWritten?.();
    },
    onError: (error) => {
      if (!isStaleRoleChangeRefusal(error)) return;
      for (const queryKey of accessWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      void queryClient.invalidateQueries({
        queryKey: orgConsoleKeys(orgRef).catalog,
        refetchType: 'none',
      });
    },
  });
}

/**
 * Take one person OFF the organization's admin team. Global-Admin only; answers 204, no body.
 *
 * ⚠ **AN ALREADY-REMOVED PERSON RESOLVES, IT DOES NOT REJECT.** The backend's 400 covers "no such
 * user", "another organization's admin" and "you already removed them" with one constant message —
 * anti-enumeration by design — and from a console reading its own roster only the last is reachable
 * in practice. The person is gone, which is what the caller asked for, so treating it as a failure
 * would put a red message on a screen whose desired end state has been reached. It still invalidates,
 * because the roster this session is looking at is the thing that is now stale.
 *
 * Every other refusal propagates — the last-Global-Admin 422 most of all, which the editor renders as
 * considered copy naming the way out.
 */
export function useRemoveOrgMember(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<void, Error, { memberId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId }: { memberId: string }) => {
      try {
        await removeOrgMember(orgRef, memberId);
      } catch (error) {
        if (!isAlreadyNotAMember(error)) throw error;
      }
    },
    onSuccess: () => {
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of removalInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      onWritten?.();
    },
  });
}

/**
 * Take YOURSELF off the organization's team — the org removal act aimed at the caller (EP-MNT9 (c)).
 *
 * ⚠ NO CACHE WORK, unlike {@link useRemoveOrgMember}. The host signs the person out next (a console
 * they no longer administer can serve them nothing), and its sign-out clears every read; refetching
 * the standing reads first would only paint "not available here" across the page for the moment
 * before the sign-out lands. "Already not a member" is a success here too: they are off the team.
 */
export function useLeaveOrganization(
  orgRef: string,
): UseMutationResult<void, Error, { selfUserId: string }> {
  return useMutation({
    mutationFn: async ({ selfUserId }: { selfUserId: string }) => {
      try {
        await removeOrgMember(orgRef, selfUserId);
      } catch (error) {
        if (!isAlreadyNotAMember(error)) throw error;
      }
    },
  });
}

/** Switch one module on or off org-wide. Answers the whole resolved map. */
export function useUpdateOrgAccessSettings(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<OrgAccessSettings | null, Error, UpdateOrgAccessSettingsBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgAccessSettingsBody) =>
      decodeOrgAccessSettings(await updateOrgAccessSettings(orgRef, body)),
    onSuccess: (updated) => {
      // Consume the SERVER's map — the PUT answers the whole resolved set, defaults already applied.
      if (updated !== null) queryClient.setQueryData(orgConsoleKeys(orgRef).settings, updated);
      for (const queryKey of accessWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      onWritten?.();
    },
  });
}

/**
 * Set the organization's measurement ids. Answers the state AFTER the write.
 *
 * The mutation resolves to the SERVER's configuration — including the fresh `updated_at` — and that
 * answer is written straight into the cache, so the form re-opens from what was actually stored
 * rather than from what it submitted. The two differ in an ordinary case: an id is normalized on the
 * way in, so `  g-abc123 ` comes back as `G-ABC123`, and a form showing its own draft would keep
 * displaying a value that is not what the organization is instrumented with.
 *
 * ⚠ NO `onAccessWritten`. That seam is for a host rendering something derived from this
 * organization's AUTHORITY — an app grid filtered by grants, a nav row gated on a switch. A
 * measurement id moves none of it, and firing the seam anyway would cost every host a request for a
 * read that cannot have changed.
 */
export function useUpdateOrgMeasurementConfig(
  orgRef: string,
): UseMutationResult<OrgMeasurementConfig | null, Error, UpdateOrgMeasurementConfigBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgMeasurementConfigBody) =>
      decodeOrgMeasurementConfig(await updateOrgMeasurementConfig(orgRef, body)),
    onSuccess: (updated) => {
      if (updated !== null) queryClient.setQueryData(orgConsoleKeys(orgRef).measurement, updated);
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of measurementWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * Offer someone administration of this organization.
 *
 * ⚠ THE MUTATION RESOLVES TO THE DISCRIMINATED ANSWER, and a caller MUST read `already_admin` rather
 * than treating a null `invite` as failure: an address that already belongs to an administrator here
 * is a SUCCESS with nothing created and nobody emailed. Handing back the whole answer — instead of
 * unwrapping to the invite — is what makes that impossible to miss at the call site.
 *
 * ⚠ ONLY THE INVITE LIST IS INVALIDATED. An offer moves nobody's access until it is redeemed, and
 * the redeem happens in the invitee's own session. See `inviteWriteInvalidations`.
 */
export function useCreateOrgInvite(
  orgRef: string,
): UseMutationResult<OrgInviteCreated | null, Error, CreateOrgInviteBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrgInviteBody) =>
      decodeOrgInviteCreated(await createOrgInvite(orgRef, body)),
    onSuccess: () => {
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of inviteWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** Rescind a live invite. Global-Admin only; answers 204 and carries no body. */
export function useRevokeOrgInvite(
  orgRef: string,
): UseMutationResult<void, Error, { inviteId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inviteId }: { inviteId: string }) => revokeOrgInvite(orgRef, inviteId),
    onSuccess: () => {
      for (const queryKey of inviteWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * Redeem an invite token → become an administrator of the inviting organization.
 *
 * ⚠ THE ONE READ IN THIS PACKAGE THAT TAKES NO `orgRef`, AND THE ONE ITS CALLER IS NOT YET AN ADMIN
 * FOR. The token resolves the organization; the caller could not name it if they wanted to.
 *
 * ⚠ **IT IS A QUERY, NOT A MUTATION, AND THAT IS THE FIX FOR A MEASURED DEFECT — NOT A STYLE
 * CHOICE.** A redeem is submitted once, automatically, the moment the page opens, and its answer has
 * to survive a REMOUNT. A `useMutation` fired from a mount effect does not: the request goes out, the
 * observer that issued it is discarded with the unmount, and the observer that comes back has lost
 * the result.
 *
 * ⚠ **TWO MECHANISMS REMOUNT THIS PAGE, and only one of them is a development artifact.** Reading
 * this as a StrictMode nicety is the wrong conclusion, so both are named:
 *
 *   1. **StrictMode's double-invoke — development.** `main.tsx` boots the app inside `<StrictMode>`,
 *      which mounts, tears down and remounts every component. Measured in a real browser on this very
 *      page, with the request confirmed to have left it: `strict=on` → one POST and a permanently
 *      pending screen; `strict=off` → one POST and the success screen.
 *   2. **A resolving-gate chrome swap — PRODUCTION, and it needs no StrictMode.** A shell gate that
 *      renders a splash INSTEAD of its children while something settles unmounts the page mid-redeem
 *      and remounts it when the answer lands. Under the old shape that is a SECOND POST of a
 *      single-use token: the membership is created and the invitee is then shown a refusal. One
 *      host's shell carries a live instance of this class; it is named, with its fix, in
 *      `agent_docs/org-console-package.md` — this package does not name hosts.
 *
 * Whether such a gate sits above a given host's accept route is a fact about THAT HOST'S ROUTE TREE,
 * never a property of this hook — which is why the cure is written to survive the mechanism rather
 * than to rely on its absence. Probed against a real accept page: a settled redeem re-renders its
 * answer across a remount with ONE POST, and a redeem still IN FLIGHT across an unmount settles on
 * the remount with ONE POST — the second mount joins the first's in-flight query, which is precisely
 * what `useMutation` cannot do.
 *
 * A fire-once guard cannot help with either mechanism: the guard is what stops the second fire, and
 * removing it would submit a SINGLE-USE token twice and refuse the second.
 *
 * A query has exactly the semantics the act needs — resolve this token once, cache the answer under
 * it, and let any later observer read that answer rather than re-asking. `staleTime: Infinity` plus
 * the three `refetchOn*: false` guards are what keep a spent token from ever being re-submitted by a
 * window focus or a reconnect; `retryOnMount: false` follows {@link useOrgSelfAccess}'s reasoning. A
 * retry is an explicit act through `refetch`, which is also the only honest place to offer one — and
 * only for a failure where asking again could change the answer.
 *
 * Nothing is invalidated here: every cache this package owns is keyed by an organization this
 * session has no standing in until the instant this succeeds. A host that renders something derived
 * from its OWN view of the caller owes that invalidation at its accept screen.
 */
export function useOrgInviteRedeem(
  token: string | null | undefined,
): UseQueryResult<OrgInviteAccepted | null> {
  const value = typeof token === 'string' ? token : '';
  return useQuery({
    queryKey: orgInviteRedeemKey(value),
    enabled: value.length > 0,
    queryFn: async () => decodeOrgInviteAccepted(await acceptOrgInvite({ token: value })),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retryOnMount: false,
  });
}

/**
 * An invite's offer, for the invite's own page to name before anyone signs in (EP-MNT17). Keyed by the
 * token, like the redeem. A read that changes nothing, so asking again is harmless and `refetch` is the
 * page's retry; `enabled` lets a host ask only while signed out, since a signed-in visitor keeps the
 * redeem's path.
 */
export function useOrgInvitePreview(
  token: string | null | undefined,
  enabled = true,
): UseQueryResult<OrgInvitePreview | null> {
  const value = typeof token === 'string' ? token : '';
  return useQuery({
    queryKey: orgInvitePreviewKey(value),
    enabled: enabled && value.length > 0,
    queryFn: async () => decodeOrgInvitePreview(await previewOrgInvite({ token: value })),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** End one connected AI's grant. Global-Admin only; answers 204 and carries no body. */
export function useRevokeOrgConnectedAi(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<void, Error, { grantId: string }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ grantId }: { grantId: string }) => revokeOrgConnectedAi(orgRef, grantId),
    onSuccess: () => {
      for (const queryKey of connectorWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      // ⚠ FIRED HERE TOO, even though a revoke cannot move a human's own grants. The seam is "a write
      // on this surface landed", not "a write that moved YOUR read" — the package cannot know which
      // is which for a host it has never seen, and a host refreshing one cached read it did not have
      // to costs a request, while a host never told costs it a stale screen.
      onWritten?.();
    },
  });
}

// ── Shell contract v3 ────────────────────────────────────────────────────────

/**
 * The WHOLE roster, exhausted — for the one screen that states a COUNT of holders per module.
 *
 * ⚠ A COUNT IS A CLAIM, and the paged roster cannot make one: `meta` is unreachable and a page is
 * not a total. So the Apps page's "N people can open it" reads the roster to exhaustion, exactly as
 * the catalog is exhausted for the editor, and says nothing until it has. Keyed as a CHILD of the
 * roster key so every write that moves the roster moves this too.
 */
export function useOrgMembersExhausted(
  orgRef: string,
  enabled = true,
): UseQueryResult<OrgConsoleMemberRow[]> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).membersAll,
    enabled,
    queryFn: async ({ signal }) => {
      const rows: OrgConsoleMemberRow[] = [];
      let offset = 0;
      for (;;) {
        const pageRows = decodeOrgMembers(await fetchOrgMembers(orgRef, { offset, signal }));
        rows.push(...pageRows);
        const next = nextOffset(pageRows.length, rows.length, ORG_CONSOLE_PAGE_LIMIT);
        if (next === undefined) return rows;
        offset = next;
      }
    },
    retry: retryUnlessAbsent,
  });
}

/**
 * The access requests — the org-wide view (`module === null`) or one module's — PENDING first
 * then newest, paged.
 *
 * ⚠ THE CALLER GATES THIS. The org-wide list needs `WRITE_MEMBER_ACCESS`; a module's list needs that
 * module's `<module>:approve_access_requests` string. Firing it on a guess spends a request the
 * server refuses, so `enabled` is the caller's settled answer — read off the standing read's
 * effective set, never off a tier.
 */
export function useOrgAccessRequests(
  orgRef: string,
  module: string | null,
  enabled = true,
): UseInfiniteQueryResult<OrgAccessRequestRow[]> {
  const queryKey = orgAccessRequestsKey(orgRef, module);
  return useInfiniteQuery({
    queryKey,
    enabled,
    queryFn: async ({ pageParam, signal }: QueryFunctionContext<typeof queryKey, number>) =>
      decodeOrgAccessRequests(
        await fetchOrgAccessRequests(orgRef, { offset: pageParam, module, signal }),
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      nextOffset(
        lastPage.length,
        allPages.reduce((n, page) => n + page.length, 0),
        ORG_CONSOLE_PAGE_LIMIT,
      ),
    select: (data) => data.pages.flat(),
    retry: retryUnlessAbsent,
  });
}

/**
 * Ask for a role in a module. Resolves to the request row (the existing open one, unchanged, when
 * a request was already open — idempotent on the server).
 *
 * Invalidates the caller's OWN standing, because that is where the tile learns it now reads
 * "Access requested" — never a second observer of the request.
 */
export function useCreateOrgAccessRequest(
  orgRef: string,
): UseMutationResult<OrgAccessRequestRow | null, Error, CreateOrgAccessRequestBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateOrgAccessRequestBody) =>
      decodeOrgAccessRequest(await createOrgAccessRequest(orgRef, body)),
    onSuccess: () => {
      // ⚠ SCOPED, AND DELIBERATELY NOT AWAITED — see `org-access-queries.ts`.
      for (const queryKey of accessRequestCreateInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** What one approval needs. `body.granted_role` overrides the requested tier when the approver changes it. */
export interface ApproveOrgAccessRequestArgs {
  requestId: string;
  body?: ApproveOrgAccessRequestBody;
}

/**
 * Approve one request — a grant through the same writer an access edit uses. Resolves to the row
 * as the server flipped it.
 *
 * Fires the host's `onAccessWritten` seam for the same reason an access edit does: a grant moved,
 * and a host drawing a surface off the caller's grants must re-read.
 */
export function useApproveOrgAccessRequest(
  orgRef: string,
  onWritten?: () => void,
): UseMutationResult<OrgAccessRequestRow | null, Error, ApproveOrgAccessRequestArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, body }: ApproveOrgAccessRequestArgs) =>
      decodeOrgAccessRequest(await approveOrgAccessRequest(orgRef, requestId, body ?? {})),
    onSuccess: () => {
      for (const queryKey of accessRequestApprovalInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      onWritten?.();
    },
  });
}

/** What one denial needs. */
export interface DenyOrgAccessRequestArgs {
  requestId: string;
  body?: DenyOrgAccessRequestBody;
}

/** Decline one request. Moves nobody's access, so no host seam fires. */
export function useDenyOrgAccessRequest(
  orgRef: string,
): UseMutationResult<OrgAccessRequestRow | null, Error, DenyOrgAccessRequestArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, body }: DenyOrgAccessRequestArgs) =>
      decodeOrgAccessRequest(await denyOrgAccessRequest(orgRef, requestId, body ?? {})),
    onSuccess: () => {
      for (const queryKey of accessRequestDenialInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * The caller's own preference definitions + values. Any admin of the organization; not gated.
 *
 * ⚠ NEVER A LANDING INPUT. The landing key rides the ONE standing read as `default_landing_module`,
 * so the root never takes a second dependency. Two readers share this key: the preferences page, and
 * a module host asking whether the viewer is served one of its module's definitions (the Compliance
 * host, for its no-recipients notice, CCM-R1-REMIND-WEB). That host reads it beside its pages, and
 * nothing waits on it.
 */
export function useOrgAdminPreferences(
  orgRef: string,
  enabled = true,
): UseQueryResult<OrgAdminPreferences | null> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).preferences,
    enabled,
    queryFn: async ({ signal }) =>
      decodeOrgAdminPreferences(await fetchOrgAdminPreferences(orgRef, { signal })),
    retry: retryUnlessAbsent,
  });
}

/**
 * Write the caller's own preferences. PARTIAL and THREE-VALUED per key; answers the whole state
 * after the write, which is written straight into the cache (no re-read of what the PUT just said).
 * Invalidates the standing read, because the landing key rides it, then tells the host which keys it
 * wrote (`OrgConsoleConfig.onPreferencesWritten`).
 */
export function useUpdateOrgAdminPreferences(
  orgRef: string,
  onPreferencesWritten?: (keys: readonly string[]) => void,
): UseMutationResult<OrgAdminPreferences | null, Error, UpdateOrgAdminPreferencesBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgAdminPreferencesBody) =>
      decodeOrgAdminPreferences(await updateOrgAdminPreferences(orgRef, body)),
    onSuccess: (updated, body) => {
      if (updated !== null) queryClient.setQueryData(orgConsoleKeys(orgRef).preferences, updated);
      for (const queryKey of preferenceWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
      onPreferencesWritten?.(Object.keys(body.values));
    },
  });
}

/**
 * The instrument one module binds for this organization, or `null` when the read answered the
 * console's 404 — the pair binds NO instrument, which is the ordinary answer for every module today.
 *
 * ⚠ 404 RESOLVES, IT DOES NOT REJECT, and that is the family's dark posture read one level down: an
 * absent instrument is a real answer ("nothing to accept"), not a failure to offer a retry for.
 * Any other failure propagates.
 */
export function useOrgModuleTerms(
  orgRef: string,
  module: string,
  enabled = true,
): UseQueryResult<OrgModuleTerms | null> {
  return useQuery({
    queryKey: orgModuleTermsKey(orgRef, module),
    enabled,
    queryFn: async ({ signal }) => {
      try {
        return decodeOrgModuleTerms(await fetchOrgModuleTerms(orgRef, module, { signal }));
      } catch (error) {
        if (isOrgConsoleUnavailable(error)) return null;
        throw error;
      }
    },
    retry: retryUnlessAbsent,
  });
}

/**
 * Accept one module's instrument at the version just read. Resolves to the terms as the server
 * re-projects them (`accepted: true`), written straight into the terms cache; invalidates the
 * settings read (its `terms_state` is now `accepted`) and the caller's standing.
 */
export function useAcceptOrgModuleTerms(
  orgRef: string,
  module: string,
): UseMutationResult<OrgModuleTerms | null, Error, AcceptOrgModuleTermsBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AcceptOrgModuleTermsBody) =>
      decodeOrgModuleTerms(await acceptOrgModuleTerms(orgRef, module, body)),
    onSuccess: (updated) => {
      if (updated !== null) queryClient.setQueryData(orgModuleTermsKey(orgRef, module), updated);
      for (const queryKey of termsAcceptInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

// ── The organization's settings — seam A (OM-17) ────────────────────────────

/**
 * The organization's setting definitions + stored values, served per caller (the class, the
 * organization's switch and the caller's effective set already applied), or `null` when the read
 * answered the console's 404 — the surface is ABSENT, which is the ordinary answer until the first
 * definition lands its backend half.
 *
 * ⚠ 404 RESOLVES, IT DOES NOT REJECT — the terms read's posture. Nothing served is a real answer
 * ("no knobs here"), not a failure to offer a retry for. Any other failure propagates.
 */
export function useOrgSettings(orgRef: string, enabled = true): UseQueryResult<OrgSettings | null> {
  return useQuery({
    queryKey: orgConsoleKeys(orgRef).orgSettings,
    enabled,
    queryFn: async ({ signal }) => {
      try {
        return decodeOrgSettings(await fetchOrgSettings(orgRef, { signal }));
      } catch (error) {
        if (isOrgConsoleUnavailable(error)) return null;
        throw error;
      }
    },
    retry: retryUnlessAbsent,
  });
}

/**
 * Whether the organization's Settings page lets this person change the setting `key`: the settings
 * read serves only what the caller is served (none while settings are dark), each with its
 * `can_change`. Read only while `enabled` (a host passes "holds `write_settings`"), so no one else
 * pays for it; `false` until known, and where `orgRef` is `null`.
 */
export function useOrgSettingChangeable(
  orgRef: string | null,
  key: string,
  enabled: boolean,
): boolean {
  const read = useOrgSettings(orgRef ?? '', enabled && orgRef !== null);
  return (
    read.data?.definitions.some(
      (definition) => definition.key === key && definition.can_change === true,
    ) === true
  );
}

/**
 * Write the organization's settings. PARTIAL and THREE-VALUED per key; answers the whole state
 * after the write, which is written straight into the cache (no re-read of what the PUT just said).
 * Invalidates the access event log — an org setting is an org act, and each changed key appends a row.
 *
 * A write that FAILS re-reads the settings: nothing was saved, and the record may have moved under
 * the page (the stale refusal, 409 `SETTING_STALE`, says it did), so the control must show what the
 * record now says rather than the value the page last read.
 */
export function useUpdateOrgSettings(
  orgRef: string,
): UseMutationResult<OrgSettings | null, Error, UpdateOrgSettingsBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateOrgSettingsBody) =>
      decodeOrgSettings(await updateOrgSettings(orgRef, body)),
    onSuccess: (updated) => {
      // An answer that does not decode leaves the read STALE, so it is re-fetched rather than trusted.
      if (updated !== null) queryClient.setQueryData(orgConsoleKeys(orgRef).orgSettings, updated);
      else void queryClient.invalidateQueries({ queryKey: orgConsoleKeys(orgRef).orgSettings });
      for (const queryKey of orgSettingWriteInvalidations(orgRef)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: orgConsoleKeys(orgRef).orgSettings });
    },
  });
}

// ── Org-account deactivation (OM-9) ─────────────────────────────────────────

/**
 * The org-account deactivation act — `POST /org/{ref}/deactivate` (Org Machinery OM-9).
 *
 * ⚠ IT INVALIDATES NOTHING, AND THAT IS THE POINT. Every other write here re-reads what it moved;
 * this one ends the CALLER's own standing along with everyone else's, so after a 200 there is no
 * read on this surface that could answer anything but the route miss. Re-reading the standing read
 * would spend a request to render a not-available notice over a console the person is being signed
 * out of. The host's `onDeactivated` does the honest thing instead: sign out, clear the cache, and
 * take the person to the class door.
 */
export function useDeactivateOrg(
  orgRef: string,
): UseMutationResult<OrgDeactivationResult | null, Error, void> {
  return useMutation({
    mutationFn: async () => decodeOrgDeactivation(await deactivateOrg(orgRef)),
  });
}
